import { addDays, isAfter, isTomorrow, sub } from 'components/helpers/dates';

export const autoEnrolmentConfigurationParamsForCreate = ({
  currentContext,
  currentCourse,
}) => {
  const {
    autoEnrolCourseRequirementScope,
    autoEnrolEnrolleeScope,
    expires: isCourseExpirySelected,
    hasAutoEnrolBeenActivelySelected,
    isELearningProviderSelected,
  } = currentCourse;

  const isEligibleCourseExpirySelection =
    currentContext.isAutoEnrolmentIncludingMissingTraining ||
    isCourseExpirySelected;

  const params = hasAutoEnrolBeenActivelySelected &&
    isELearningProviderSelected &&
    isEligibleCourseExpirySelection && {
      enrollee_scope: autoEnrolEnrolleeScope,
      course_requirement_scope: autoEnrolCourseRequirementScope,
    };

  // Explicitly returning null to prevent backend errors when submitting params
  return params || null;
};

export const autoEnrolmentConfigurationParamsForUpdate = ({
  currentContext,
  currentCourse,
  domainAutoEnrolmentConfiguration,
}) => {
  const {
    autoEnrolCourseRequirementScope,
    autoEnrolEnrolleeScope,
    expires: isCourseExpirySelected,
    hasAutoEnrolBeenActivelySelected,
    isELearningProviderSelected,
  } = currentCourse;

  const domainAutoEnrolmentConfigurationId =
    domainAutoEnrolmentConfiguration?.id;
  const { isAutoEnrolmentIncludingMissingTraining } = currentContext;

  const actionRequired = (function determineActionRequired() {
    if (
      (function shouldDelete() {
        return (
          domainAutoEnrolmentConfigurationId &&
          (!hasAutoEnrolBeenActivelySelected ||
            (!isAutoEnrolmentIncludingMissingTraining &&
              !isCourseExpirySelected) ||
            !isELearningProviderSelected)
        );
      })()
    ) {
      return 'delete';
    } else if (
      (function shouldCreateOrUpdate() {
        return (
          hasAutoEnrolBeenActivelySelected &&
          (isAutoEnrolmentIncludingMissingTraining || isCourseExpirySelected) &&
          isELearningProviderSelected
        );
      })()
    ) {
      return domainAutoEnrolmentConfigurationId ? 'update' : 'create';
    }
  })();

  const params = (function determineParams() {
    const attributes = {
      enrollee_scope: autoEnrolEnrolleeScope,
      course_requirement_scope: autoEnrolCourseRequirementScope,
    };

    switch (actionRequired) {
      case 'delete':
        return { id: domainAutoEnrolmentConfigurationId, _destroy: 1 };
      case 'create':
        return attributes;
      case 'update':
        return { ...attributes, id: domainAutoEnrolmentConfigurationId };
      default:
        return null;
    }
  })();

  return params;
};

export const mappedAutoEnrolmentExclusionsByConfigurationId = (
  autoEnrolmentExclusions,
) =>
  Object.fromEntries(
    Object.values(autoEnrolmentExclusions).map((value) => [
      value.relationships.autoEnrolmentConfiguration.data.id,
      value,
    ]),
  );

export const prioritisedAutoEnrolmentBlockers = (autoEnrolmentDependencies) => {
  const {
    autoEnrolmentConfiguration,
    currentELearningCredits,
    isAutoEnrolmentIncludingMissingTraining,
    isBookingAllowedWithinTrackedPersonnelLimits,
    isCreditInfoUnavailable,
    isELearningNonCustom,
    isExpiringCourse,
    isFromAutoEnrollableDivision,
    isPersonnelEmailUnavailable,
    isRequired,
    isSubcontractor,
    training,
  } = autoEnrolmentDependencies;

  const blockers = [];

  if (!isFromAutoEnrollableDivision)
    blockers.push('personnelNotFromAutoEnrollableDivision');
  if (!autoEnrolmentConfiguration)
    blockers.push('noAutoEnrolmentConfiguration');
  if (!isBookingAllowedWithinTrackedPersonnelLimits)
    blockers.push('trackedPersonnelLimit');

  if (isELearningNonCustom && isCreditInfoUnavailable)
    blockers.push('creditInfoUnavailable');
  if (isPersonnelEmailUnavailable) blockers.push('personnelEmailUnavailable');
  if (
    isELearningNonCustom &&
    !isCreditInfoUnavailable &&
    currentELearningCredits <= 0
  )
    blockers.push('insufficientCredits');

  const isAnEligibleTrainingInclusion = isEligibleTrainingInclusion({
    isAutoEnrolmentIncludingMissingTraining,
    training,
  });
  if (!isAnEligibleTrainingInclusion) blockers.push('invalidTrainingInclusion');

  if (
    autoEnrolmentConfiguration &&
    !isEligibleEmployeeScope({ autoEnrolmentConfiguration, isSubcontractor })
  )
    blockers.push('enrolleeScope');
  if (
    autoEnrolmentConfiguration &&
    !isEligibleCourseRequirementScope({
      autoEnrolmentConfiguration,
      isRequired,
    })
  )
    blockers.push('courseRequirementScope');

  if (
    !isEligibleCourseExpiry({
      isAutoEnrolmentIncludingMissingTraining,
      training,
      isExpiringCourse,
    })
  )
    blockers.push('invalidCourseExpiry');
  if (
    isAnEligibleTrainingInclusion &&
    !isEligibleTrainingExpiryDate({
      isAutoEnrolmentIncludingMissingTraining,
      training,
    })
  )
    blockers.push('invalidTrainingExpiryDate');

  return blockers;
};

export const hasOnlyNonCriticalBlockers = ({
  prioritisedAutoEnrolmentBlockers,
}) => {
  if (prioritisedAutoEnrolmentBlockers.length === 0) return false;

  const nonCriticalBlockers = [
    'creditInfoUnavailable',
    'personnelEmailUnavailable',
    'insufficientCredits',
  ];

  return prioritisedAutoEnrolmentBlockers.every((blocker) =>
    nonCriticalBlockers.includes(blocker),
  );
};

export const autoEnrolmentNonCriticalBlockersStatusText = ({
  priorityBlocker,
  proposedEnrolmentDate,
  translations,
}) => {
  if (
    ['creditInfoUnavailable', 'personnelEmailUnavailable'].includes(
      priorityBlocker,
    )
  ) {
    return translations[priorityBlocker];
  }

  if (proposedEnrolmentDate) {
    const isProposedEnrolmentDateTomorrow = isTomorrow({
      date: proposedEnrolmentDate,
    });

    if (priorityBlocker === 'insufficientCredits') {
      return translations.insufficientCredits[
        isProposedEnrolmentDateTomorrow ? 'tomorrow' : 'date'
      ];
    }

    if (!priorityBlocker) {
      return translations.noBlocker[
        isProposedEnrolmentDateTomorrow ? 'tomorrow' : 'date'
      ];
    }
  } else {
    return '';
  }
};

export const proposedAutoEnrolmentDate = ({
  courseExpiringDurationParts,
  latestTraining,
}) => {
  const dateToday = new Date();
  const dateTomorrow = addDays({ amount: 1, date: dateToday });

  if (!latestTraining) return dateTomorrow;

  const proposedAutoEnrolmentDate = sub({
    date: latestTraining.attributes.expiryDate,
    durationParts: courseExpiringDurationParts,
  });

  return (
      isAfter({ date: proposedAutoEnrolmentDate, dateToCompare: dateToday })
    ) ?
      proposedAutoEnrolmentDate
    : dateTomorrow;
};

// private

const isEligibleTrainingInclusion = ({
  isAutoEnrolmentIncludingMissingTraining,
  training,
}) => !!(training || (isAutoEnrolmentIncludingMissingTraining && !training));

const isEligibleEmployeeScope = ({
  autoEnrolmentConfiguration,
  isSubcontractor,
}) =>
  !!(
    autoEnrolmentConfiguration?.attributes.enrolleeScope === 'all' ||
    (autoEnrolmentConfiguration?.attributes.enrolleeScope === 'employees' &&
      !isSubcontractor)
  );

const isEligibleCourseRequirementScope = ({
  autoEnrolmentConfiguration,
  isRequired,
}) =>
  !!(
    autoEnrolmentConfiguration?.attributes.courseRequirementScope === 'all' ||
    (autoEnrolmentConfiguration?.attributes.courseRequirementScope ===
      'required' &&
      isRequired)
  );

const isEligibleCourseExpiry = ({
  isAutoEnrolmentIncludingMissingTraining,
  isExpiringCourse,
  training,
}) =>
  isExpiringCourse || (isAutoEnrolmentIncludingMissingTraining && !training);

const isEligibleTrainingExpiryDate = ({
  isAutoEnrolmentIncludingMissingTraining,
  training,
}) =>
  training?.attributes?.expiryDate ||
  (isAutoEnrolmentIncludingMissingTraining && !training);
